import { useCallback, useMemo } from "react";
import { clsx } from "clsx";
import { useSelector } from "react-redux";
import Select from "react-select";

import { ResponseMode } from "../../const/const";
import { selectStyles } from "../../const/selectStyles";
import { TarotCardNames } from "../../const/tarot";
import { fetchTarotImages } from "../../model/services/fetchTarotImages";
import { readingDetailsActions } from "../../model/slices/readingDetailsSlice";
import { getResponseFormData } from "../../model/selectors/getResponseFormSelectors";

import { UserPicture } from "@/entities/User";
import { useAppDispatch } from "@/shared/lib/hooks/redux";
import { Tarot } from "@/entities/Tarot";
import { getPersonalityListData, getPersonalityListIsLoading } from "@/entities/Personality";

import cls from "./ResponseOptions.module.scss";

export const ResponseOptions = () => {
  const dispatch = useAppDispatch();

  const personalityListIsLoading = useSelector(getPersonalityListIsLoading);
  const personalityListData = useSelector(getPersonalityListData);
  const responseForm = useSelector(getResponseFormData);

  const onChangePersonality = useCallback(
    ({ value }) => {
      dispatch(readingDetailsActions.setResponseFormPersonalityId(value));
    },
    [dispatch]
  );

  const onChangeTarotCardName = useCallback(
    async ({ value }) => {
      dispatch(readingDetailsActions.setResponseFormTarotCardName(value));
      dispatch(fetchTarotImages(value));
    },
    [dispatch]
  );

  const onSelectTarotCard = useCallback(
    (card: Tarot) => () => {
      dispatch(readingDetailsActions.setResponseFormTarotCard(card));
    },
    [dispatch]
  );

  const personalityOptions = useMemo(() => {
    return personalityListData?.map((el) => ({
      value: el.id,
      label: el.firstName,
    }));
  }, [personalityListData]);

  const selectedPersonalityOption = personalityOptions.find(({ value }) => {
    return value === responseForm.writerPersonality;
  });

  const tarotOptions = TarotCardNames.map((card) => ({
    value: card,
    label: card,
  }));

  const selectedTarotCardNameOption = tarotOptions.find(({ value }) => {
    return String(value) === String(responseForm.tarotCardName);
  });

  const selectedPersonality = personalityListData.find((item) => item.id === responseForm.writerPersonality);

  return (
    <div className={cls.ResponseOptions}>
      <div className={cls.DropdownsList}>
        <div className={cls.DropdownWrapper}>
          <div className={cls.DropdownLabel}>This Message's Personality</div>
          <Select
            styles={selectStyles}
            options={personalityOptions}
            value={selectedPersonalityOption}
            onChange={onChangePersonality}
            isLoading={personalityListIsLoading}
            isMulti={false}
            isClearable={false}
            isSearchable
            menuPlacement="top"
            closeMenuOnSelect
            components={{ IndicatorSeparator: null }}
          />
        </div>
        {responseForm.responseMode === ResponseMode.ReadingResponse && (
          <div className={cls.DropdownWrapper}>
            <div className={cls.DropdownLabel}>This Message's Tarot Card</div>
            <Select
              styles={selectStyles}
              options={tarotOptions}
              value={selectedTarotCardNameOption}
              onChange={onChangeTarotCardName}
              isMulti={false}
              isClearable={false}
              isSearchable
              menuPlacement="top"
              closeMenuOnSelect
              components={{ IndicatorSeparator: null }}
            />
          </div>
        )}
        <UserPicture className={cls.ProfilePicture} src={selectedPersonality?.picture} />
      </div>
      {responseForm.tarotCardName && !!responseForm.allowedTarotCards.length && (
        <div className={cls.TarotCardsWrapper}>
          <div className={cls.TarotCards}>
            {responseForm.allowedTarotCards.map((card, i) => (
              <div
                key={`${i}${card.id}`}
                className={clsx(cls.Option, { [cls.isSelected]: responseForm.tarotCard?.id === card.id })}
                onClick={onSelectTarotCard(card)}
              >
                <img src={card.image} alt="Card" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
